import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { products } from '../data/products';
import Badge from '../components/Badge';
import { Product } from '../types';

const BadgeValidationPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchType, setSearchType] = useState<'code' | 'upc' | 'name'>('code');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) return;
    
    setIsLoading(true);
    setHasSearched(true);
    
    // Simulate API call with timeout
    setTimeout(() => {
      let results: Product[] = [];
      
      switch (searchType) {
        case 'code':
          results = products.filter(product => 
            product.badge?.code?.toLowerCase() === searchQuery.toLowerCase()
          );
          break;
        case 'upc':
          results = products.filter(product => 
            product.upc?.toLowerCase() === searchQuery.toLowerCase()
          );
          break;
        case 'name':
          results = products.filter(product => 
            product.name.toLowerCase().includes(searchQuery.toLowerCase())
          );
          break;
      }
      
      setSearchResults(results);
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-3xl font-bold text-gray-900 mb-4">GoodCart Badge Validation</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Verify the authenticity of GoodCart badges and discover products that meet our highest quality standards.
            </p>
          </motion.div>
          
          {/* Badge Explanation */}
          <motion.div 
            className="bg-white rounded-lg shadow-md p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">About GoodCart Badges</h2>
            <p className="text-gray-600 mb-6">
              GoodCart badges are awarded to products that meet our strict quality standards. The "GoodCart Perfect" badge is our highest honor, 
              awarded only to products that achieve a 100% score across all our evaluation criteria.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">GoodCart Perfect</h3>
                <div className="flex items-center mb-2">
                  <Badge 
                    badge={{
                      type: 'perfect',
                      code: 'EXAMPLE',
                      issueDate: new Date().toISOString()
                    }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Products with a 100% score across all evaluation criteria, including ingredient quality, 
                  sustainability, ethical sourcing, and health benefits.
                </p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">Other Badges</h3>
                <div className="flex flex-wrap gap-2 mb-2">
                  <Badge 
                    badge={{
                      type: 'verified',
                      code: 'EXAMPLE',
                      issueDate: new Date().toISOString()
                    }}
                    size="sm"
                  />
                  <Badge 
                    badge={{
                      type: 'organic',
                      code: 'EXAMPLE',
                      issueDate: new Date().toISOString()
                    }}
                    size="sm"
                  />
                  <Badge 
                    badge={{
                      type: 'sustainable',
                      code: 'EXAMPLE',
                      issueDate: new Date().toISOString()
                    }}
                    size="sm"
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Products may qualify for specific badges based on certifications, 
                  production methods, and other quality indicators.
                </p>
              </div>
            </div>
          </motion.div>
          
          {/* Search Form */}
          <motion.div 
            className="bg-white rounded-lg shadow-md p-6 mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Validate a Badge</h2>
            <form onSubmit={handleSearch}>
              <div className="flex flex-col md:flex-row gap-4 mb-4">
                <div className="flex-1">
                  <label htmlFor="searchType" className="block text-sm font-medium text-gray-700 mb-1">
                    Search by
                  </label>
                  <select
                    id="searchType"
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as any)}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                  >
                    <option value="code">Badge Code</option>
                    <option value="upc">UPC</option>
                    <option value="name">Product Name</option>
                  </select>
                </div>
                
                <div className="flex-1">
                  <label htmlFor="searchQuery" className="block text-sm font-medium text-gray-700 mb-1">
                    {searchType === 'code' ? 'Enter Badge Code' : 
                     searchType === 'upc' ? 'Enter UPC' : 'Enter Product Name'}
                  </label>
                  <input
                    type="text"
                    id="searchQuery"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={searchType === 'code' ? 'e.g. GCP12345' : 
                                searchType === 'upc' ? 'e.g. 123456789012' : 'e.g. Organic Coconut Oil'}
                    className="w-full rounded-md border border-gray-300 py-2 px-3 focus:outline-none focus:ring-2 focus:ring-brand-green-500 focus:border-brand-green-500"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                className="w-full md:w-auto btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Validating...
                  </>
                ) : 'Validate'}
              </button>
            </form>
          </motion.div>
          
          {/* Search Results */}
          {hasSearched && (
            <motion.div 
              className="bg-white rounded-lg shadow-md p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Results</h2>
              
              {isLoading ? (
                <div className="py-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-brand-green-500"></div>
                  <p className="mt-2 text-gray-600">Searching...</p>
                </div>
              ) : searchResults.length > 0 ? (
                <div className="space-y-6">
                  {searchResults.map(product => (
                    <div key={product.id} className="border border-gray-200 rounded-lg p-4 flex flex-col md:flex-row gap-4">
                      <div className="w-full md:w-1/4">
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-40 object-cover rounded-lg"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.src = `https://placehold.co/300x300@3x?text=${product.name.replace(/ /g, '+')}&font=poppins`;
                          }}
                        />
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
                          {product.badge && <Badge badge={product.badge} />}
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-4">{product.description}</p>
                        
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-gray-500">Health Score</p>
                            <p className="font-medium">{product.healthScore}/100</p>
                          </div>
                          
                          <div>
                            <p className="text-sm text-gray-500">Rating</p>
                            <p className="font-medium">{product.rating} ★</p>
                          </div>
                          
                          {product.upc && (
                            <div>
                              <p className="text-sm text-gray-500">UPC</p>
                              <p className="font-medium">{product.upc}</p>
                            </div>
                          )}
                          
                          {product.badge && (
                            <div>
                              <p className="text-sm text-gray-500">Badge Code</p>
                              <p className="font-medium">{product.badge.code}</p>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-2">
                          <Link to={`/products/${product.id}`} className="btn btn-sm btn-outline">
                            View Details
                          </Link>
                          
                          {product.badge && product.badge.type === 'perfect' && (
                            <div className="bg-brand-green-50 text-brand-green-800 text-xs px-3 py-1 rounded-full flex items-center">
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                              </svg>
                              Verified Perfect Score
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="py-8 text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="text-lg font-medium text-gray-900 mb-1">No results found</h3>
                  <p className="text-gray-600">
                    We couldn't find any products matching your search criteria.
                  </p>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BadgeValidationPage;